import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { ChatProvider } from '@/contexts/ChatContext';
import { AuthProvider } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { AuthModal } from '@/components/auth/AuthModal';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import AgentPreviewPage from '@/pages/AgentPreviewPage';
import SequencerDemoPage from '@/pages/SequencerDemoPage';
import WorkflowsPage from '@/pages/WorkflowsPage';
import VaultPage from '@/pages/VaultPage';
import { V2Layout } from '@/components/V2Layout';
import SettingsPage from '@/pages/SettingsPage';
import TrustPage from '@/pages/TrustPage';
import { TermsPage } from '@/pages/TermsPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import ProfilePage from '@/pages/ProfilePage';
import LandingPage from '@/pages/LandingPage';
import IdleLockScreen from '@/components/IdleLockScreen';
import AdminPage from '@/pages/AdminPage';
import DraftLibraryPage from '@/pages/DraftLibraryPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import SetupAccountPage from '@/pages/SetupAccountPage';
import BlogIndex from '@/pages/BlogIndex';
import BlogPost from '@/pages/BlogPost';
import TemplatesPage from '@/pages/TemplatesPage';
import GuidancePage from '@/pages/GuidancePage';
import NotFoundPage from '@/pages/NotFoundPage';
import WaitlistPage from '@/pages/WaitlistPage';
import SupportConsole from '@/pages/SupportConsole';
import TryPage, { TryResultsPage } from '@/pages/TryPage';
import AcceptInvitePage from '@/pages/AcceptInvitePage';
const StaticLandingRoute = lazy(() => import('@/components/StaticLandingRoute'));
import HeraldButton from '@/components/herald/HeraldButton';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useMobileViewport } from '@/hooks/useMobileViewport';

// Mobile viewport initializer component
const MobileViewportHandler = ({ children }) => {
  const { viewportHeight } = useMobileViewport();
  
  return <>{children}</>;
};

// Auth-gated wrapper for the floating Herald pill.
// Lives inside <AuthProvider> so useAuth() is valid here. Mirrors the
// component's own internal short-circuit so the marketing site stays
// pristine even if a stale Supabase session briefly hydrates `user`.
const AuthedHeraldButton = () => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <HeraldButton /> : null;
};

const ScrollManager = () => {
  const location = useLocation();

  useEffect(() => {
    const isChatRoute = location.pathname.startsWith('/chat');

    document.body.classList.toggle('chat-locked', isChatRoute);
    document.documentElement.classList.toggle('chat-locked', isChatRoute);

    return () => {
      document.body.classList.remove('chat-locked');
      document.documentElement.classList.remove('chat-locked');
    };
  }, [location.pathname]);

  return null;
};

// Chat app with protected route — V3 design at /chat.
// ErrorBoundary wraps AgentPreviewPage so a runtime crash inside the
// chat surface never leaves users staring at a blank page (production
// incident May-12 2026: a TDZ ReferenceError in AgentPreviewPage took
// down the entire /chat route and users could not even reach the
// sign-in modal mounted below it).
const ChatApp = () => (
  <V2Layout>
    <ErrorBoundary>
      <AgentPreviewPage />
    </ErrorBoundary>
    <AuthModal />
  </V2Layout>
);

// Landing page wrapper with auth modal
const LandingWithAuth = () => (
  <>
    <LandingPage />
    <AuthModal />
  </>
);

// Phase 1 routing split (Jun 2026): the static marketing landing owns "/"
// at the edge (Cloudflare rewrite → /landing/index.html), so the landing's
// "Sign in" link needs a server-reachable auth entry. /signin renders the
// same LandingWithAuth surface with the AuthModal pre-opened.
// Routing-only: no auth, session, or redirect-URL changes.
const SignInLanding = () => {
  const { setIsAuthModalOpen } = useAuth();
  // Jul 2026 fix: the idle tab-lock redirects to `/signin?idle=1`
  // (AuthContext idle timer), but the IdleLockScreen gate only lived
  // on `/` — so locked tabs got the OLD marketing landing + AuthModal
  // instead of the lock screen. Gate this route the same way.
  let tabLocked = false;
  try {
    tabLocked = new URLSearchParams(window.location.search).get('idle') === '1'
      || sessionStorage.getItem('bayenah_tab_locked') === '1';
  } catch (_e) { /* storage disabled */ }
  useEffect(() => {
    if (!tabLocked) setIsAuthModalOpen(true);
  }, [setIsAuthModalOpen, tabLocked]);
  if (tabLocked) return <IdleLockScreen />;
  return <LandingWithAuth />;
};

// Renders the public landing for anonymous visitors. When the visitor
// is ALREADY authenticated (returning user, post-login redirect, or a
// stray navigate('/') from anywhere in the app), bounces them straight
// into /chat instead of showing the marketing page. This is the second
// half of the Sprint 11.3 (2026-06-13) fix that closed the post-login
// landing loop — the first half is the localStorage probe in App()
// below that prevents the static-landing iframe from intercepting
// authenticated traffic at /.
const LandingOrChatGate = () => {
  const { isAuthenticated, loading } = useAuth();
  // Jul 2026: the idle tab-lock lands here as `/?idle=1`. Previously it
  // rendered the OLD React marketing landing (theme mismatch vs the
  // static edge landing, with Herald floating on it). A locked tab now
  // gets a dedicated minimal lock screen instead of any marketing page.
  let tabLocked = false;
  try {
    tabLocked = sessionStorage.getItem('bayenah_tab_locked') === '1'
      || new URLSearchParams(window.location.search).get('idle') === '1';
  } catch (_e) { /* storage disabled */ }
  if (tabLocked) return <IdleLockScreen />;
  // While Supabase is still rehydrating the session from storage, show
  // nothing for a beat rather than briefly flashing the landing. The
  // probe is bounded by AuthProvider's own getSession() timeout.
  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/chat" replace />;
  return <LandingWithAuth />;
};

function App() {
  // ── Static v3 motion landing intercept ──────────────────────────────
  // For the literal landing paths only, return the iframe-rendered
  // static landing BEFORE the Router mounts. Every other path
  // (`/signin`, `/agent`, `/admin`, `/api/*`, ...) falls through to
  // the existing Router tree untouched. The landing chunk is lazy so
  // the ~30 KB gzipped HTML string ships only when a visitor actually
  // lands on `/` — app-route visitors never download it.
  //
  // Sprint 11.3 (2026-06-13) — the early-return is now also gated on
  // "no persisted Supabase session". If localStorage has any
  // `sb-*-auth-token` key, the visitor is authenticated (or about to
  // rehydrate as such) and the static landing must NOT intercept
  // them: it bypasses BrowserRouter/AuthProvider entirely and traps
  // them on /, which produced the post-login redirect loop reported
  // on 2026-06-13 ("chat appears then refreshes to landing").
  // Authed traffic falls through to the Router, where `/` resolves to
  // <LandingOrChatGate> which Navigate-replaces to /chat. Synchronous
  // probe — no flicker.
  //
  // Diagnostic-only escape hatch (`/__landing-test`) is included so
  // QA can screenshot the iframe path against the direct
  // `/landing/index.html` baseline without depending on the craco
  // dev-server's `/` middleware. Safe to keep — the path is
  // namespaced and never linked from anywhere.
  if (typeof window !== 'undefined') {
    const p = window.location.pathname;
    const isLandingPath = (p === '/' || p === '/ar' || p === '/ar/' || p === '/__landing-test');
    // Always allow the explicit diagnostic path through to the iframe,
    // regardless of auth state — QA needs to be able to compare it.
    const isDiagnostic = p === '/__landing-test';
    let hasPersistedSession = false;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
          hasPersistedSession = true;
          break;
        }
      }
    } catch (_e) { /* storage disabled — treat as unauthed */ }
    if (isLandingPath && (isDiagnostic || !hasPersistedSession)) {
      return (
        <Suspense
          fallback={
            <div
              style={{ position: 'fixed', inset: 0, background: '#0a0a0a' }}
              data-testid="static-landing-suspense-fallback"
            />
          }
        >
          <StaticLandingRoute />
        </Suspense>
      );
    }
  }

  return (
    <BrowserRouter>
      <ScrollManager />
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <ChatProvider>
              <TooltipProvider>
                <MobileViewportHandler>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<LandingOrChatGate />} />
                    <Route path="/signin" element={<SignInLanding />} />
                    <Route path="/__sequencer_demo__" element={<SequencerDemoPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/blog" element={<BlogIndex />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    {/* Public — user arrives with a fresh magic-link session but
                        no password yet. SetupAccountPage gates on `isAuthenticated`
                        internally and bounces to / if there's no session. */}
                    <Route path="/setup-account" element={<SetupAccountPage />} />
                    
                    {/* Protected routes (require authentication) */}
                    <Route path="/chat" element={
                      <ProtectedRoute>
                        <ChatApp />
                      </ProtectedRoute>
                    } />
                    <Route path="/chat/vault" element={
                      <ProtectedRoute>
                        <V2Layout><VaultPage /></V2Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/chat/workflows" element={
                      <ProtectedRoute>
                        <V2Layout><WorkflowsPage /></V2Layout>
                      </ProtectedRoute>
                    } />
                    {/* Cluster E.1.a (Feb 2026): canonical matter
                        deep-link. Declared AFTER the static
                        /chat/{vault,workflows} so react-router v6
                        matches them first (live-tree map § 1.d
                        ordering constraint). The component is the
                        same <ChatApp>; the consumer (AgentPreviewPage
                        useParams().matter_id) lands in E.1.b. Until
                        then this route resolves but does NOT
                        rehydrate the envelope — it shows the default
                        /chat view. */}
                    <Route path="/chat/:matter_id" element={
                      <ProtectedRoute>
                        <ChatApp />
                      </ProtectedRoute>
                    } />

                    {/* Legacy /v2/* — permanent redirects to /chat. */}
                    <Route path="/v2" element={<Navigate to="/chat" replace />} />
                    <Route path="/v2/vault" element={<Navigate to="/chat/vault" replace />} />
                    <Route path="/v2/workflows" element={<Navigate to="/chat/workflows" replace />} />

                    {/* Cluster F.5 (Feb 2026): support-role break-glass console.
                        Server enforces role on every endpoint; the page
                        renders a "Not authorized" message when probe → 403. */}
                    <Route path="/_support" element={
                      <ProtectedRoute>
                        <SupportConsole />
                      </ProtectedRoute>
                    } />

                    <Route path="/profile" element={
                      <ProtectedRoute>
                        <ProfilePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/library" element={
                      <ProtectedRoute>
                        <DraftLibraryPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/draft-library" element={<Navigate to="/library" replace />} />
                    <Route path="/templates" element={
                      <ProtectedRoute>
                        <V2Layout><TemplatesPage /></V2Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/guidance" element={
                      <ProtectedRoute>
                        <V2Layout><GuidancePage /></V2Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/settings" element={
                      <ProtectedRoute>
                        <V2Layout><SettingsPage /></V2Layout>
                      </ProtectedRoute>
                    } />
                    <Route path="/trust" element={<TrustPage />} />
                    <Route path="/waitlist" element={<WaitlistPage />} />
                    <Route path="/try" element={<TryPage />} />
                    <Route path="/try/results" element={<TryResultsPage />} />
                    <Route path="/accept-invite" element={<AcceptInvitePage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </MobileViewportHandler>
                {/* Floating Herald pill — visible only when auth'd
                    (the component itself short-circuits on no-user). */}
                <AuthedHeraldButton />
                <Toaster position="top-center" />
              </TooltipProvider>
            </ChatProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
